import React, { useMemo } from 'react';
import { AnswerChart } from './AnswerChart';
import { Criterion } from '@app/api';

const maxLimit = 100;

interface Props {
  title?: string;
  range: {
    step: number;
    label?: string;
    min?: number;
    max?: number;
  };
  data?: Record<string, Record<string, number>>;
  statusList?: string[];
  criterion?: Criterion;
}

export const RangeAnswerChart = ({
  title,
  range,
  data,
  statusList,
  criterion,
}: Props) => {
  const { min, max } = useMemo(() => {
    const min = range.min;
    const max = range.max;

    if (min !== undefined && max !== undefined) {
      return { min, max };
    }

    return { min: 18, max: maxLimit };
  }, [range.max, range.min]);

  const step = range.step;

  const choices = useMemo(() => {
    if (criterion && criterion.quotas && criterion.quotas.length > 0) {
      return criterion.quotas.map((quota) => {
        const item = quota.item as { min?: number; max?: number };
        const min = item.min ?? 0;
        const max = item.max ?? maxLimit;
        return {
          label: `${min} - ${max}`,
          value: String(min),
          range: [min, max],
          quota: quota.value,
        };
      });
    }

    const start = min % step === 0 ? min + step : min + (step - (min % step));
    const choices = [
      {
        label: `${min} - ${start}`,
        value: String(min),
        range: [min, start],
      },
    ];

    for (let i = start; i <= max; i += step) {
      choices.push({
        label: `${i} - ${i + step}`,
        value: String(i),
        range: [i, i + step],
      });
    }
    return choices;
  }, [criterion, min, step, max]);

  const aggData = useMemo(() => {
    if (!data) {
      return {};
    }
    const keys = Object.keys(data).map(Number).sort();
    const aggData: Record<string, Record<string, number>> = {};

    let choiceIndex = 0;
    let keyIndex = 0;

    while (choiceIndex < choices.length && keyIndex < keys.length) {
      const choice = choices[choiceIndex];
      const [min, max] = choice.range;
      const value = keys[keyIndex];

      const originalData = data[value] || data[String(value)];
      if (!originalData) {
        keyIndex++;
        continue;
      }

      const minKey = String(min);
      if (!aggData[minKey]) {
        aggData[minKey] = {};
      }

      if (value < min || value > maxLimit) {
        keyIndex++;
        continue;
      }

      if (criterion?.quotas && criterion.quotas.length > 0) {
        if (value > max) {
          choiceIndex++;
          continue;
        }
      } else if (value >= max) {
        choiceIndex++;
        continue;
      }

      for (const [status, count] of Object.entries(originalData)) {
        if (!aggData[minKey][status]) {
          aggData[minKey][status] = count;
        } else {
          aggData[minKey][status] += count;
        }
      }

      keyIndex++;
    }
    return aggData;
  }, [data, choices, criterion?.quotas]);

  return (
    <AnswerChart
      statusList={statusList}
      title={title}
      choices={choices}
      data={aggData}
    />
  );
};
